import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';
import {
  BarChart3, TrendingUp, Users, Building2, DollarSign,
  Trophy, Calendar, ArrowRight
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart,
  Area, Legend
} from 'recharts';

const CURRENCY_SYMBOLS = { TRY: '₺', USD: '$', EUR: '€', GBP: '£' };

const PIE_COLORS = {
  'Satıldı': '#ef4444',
  'Rezerve': '#f59e0b',
  'Müsait': '#22c55e',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs">
      {label && <p className="font-semibold text-gray-700 mb-1">{label}</p>}
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color }}>
          {entry.name}: <strong>{typeof entry.value === 'number' ? entry.value.toLocaleString('tr-TR') : entry.value}</strong>
        </p>
      ))}
    </div>
  );
};

export default function TenantDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/dashboard/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch {
      toast.error('Dashboard yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-8 text-gray-400">Yükleniyor...</div>;
  if (!data) return <div className="p-8 text-red-500">Dashboard yüklenemedi</div>;

  const { summary, revenue_total, top_customers_revenue, top_customers_exhibitions, top_salespeople, exhibition_comparison, monthly_sales } = data;

  // Pie chart data for occupancy
  const occupancyPieData = [
    { name: 'Satıldı', value: summary.sold },
    { name: 'Rezerve', value: summary.reserved },
    { name: 'Müsait', value: summary.available },
  ].filter(d => d.value > 0);

  // Exhibition bar chart data (top 8)
  const exhibitionBarData = exhibition_comparison
    .slice(0, 8)
    .map(ex => ({
      name: ex.name.length > 16 ? ex.name.slice(0, 14) + '…' : ex.name,
      fullName: ex.name,
      Satıldı: ex.sold,
      Rezerve: ex.reserved,
      Müsait: ex.available,
      occupancy: ex.occupancy_pct,
      id: ex.id,
    }));

  // Monthly sales chart data
  const monthlyChartData = monthly_sales.map(m => ({
    month: m.month.replace('-', '/'),
    'Satış': m.sold_count,
  }));

  // Salespeople bar chart
  const salesBarData = top_salespeople.slice(0, 6).map(sp => ({
    name: sp.name.length > 12 ? sp.name.slice(0, 10) + '…' : sp.name,
    'Satış': sp.sold,
    'Rezerve': sp.reserved,
  }));

  return (
    <div className="p-6 space-y-6" data-testid="tenant-dashboard">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <BarChart3 className="w-8 h-8 text-teal-600" />
          Genel Dashboard
        </h1>
        <p className="text-gray-500 mt-1">Tüm fuarlarınızın birleşik görünümü</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="summary-cards">
        {[
          { label: 'Fuarlar', value: summary.total_exhibitions, icon: Calendar, bg: 'bg-slate-50', color: '' },
          { label: 'Standlar', value: summary.total_stands, icon: Building2, bg: 'bg-blue-50', color: '' },
          { label: 'Firmalar', value: summary.total_companies, icon: Users, bg: 'bg-emerald-50', color: '' },
          { label: 'Satıldı', value: summary.sold, icon: TrendingUp, bg: 'bg-red-50', color: 'text-red-700' },
          { label: 'Doluluk', value: `${summary.occupancy_pct}%`, icon: TrendingUp, bg: 'bg-amber-50', color: 'text-amber-700' },
        ].map(({ label, value, icon: Icon, bg, color }) => (
          <Card key={label} className={`${bg} border-0`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                <Icon className="w-4 h-4" /> {label}
              </div>
              <p className={`text-3xl font-bold ${color}`}>{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Occupancy Pie + Revenue */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Donut chart for occupancy */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-teal-600" /> Stand Durumu Dağılımı
            </CardTitle>
          </CardHeader>
          <CardContent>
            {summary.total_stands > 0 ? (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="50%" height={160}>
                  <PieChart>
                    <Pie
                      data={occupancyPieData}
                      cx="50%" cy="50%"
                      innerRadius={45} outerRadius={70}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {occupancyPieData.map((entry) => (
                        <Cell key={entry.name} fill={PIE_COLORS[entry.name]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-3 flex-1">
                  {occupancyPieData.map(entry => (
                    <div key={entry.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PIE_COLORS[entry.name] }} />
                        <span className="text-sm text-gray-600">{entry.name}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-bold">{entry.value}</span>
                        <span className="text-xs text-gray-400 ml-1">
                          ({summary.total_stands > 0 ? Math.round(entry.value / summary.total_stands * 100) : 0}%)
                        </span>
                      </div>
                    </div>
                  ))}
                  <div className="pt-1">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Genel Doluluk</span>
                      <span className="font-bold text-teal-600">{summary.occupancy_pct}%</span>
                    </div>
                    <Progress value={summary.occupancy_pct} className="h-2" />
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-gray-400 text-sm text-center py-8">Henüz stand yok</p>
            )}
          </CardContent>
        </Card>

        {/* Revenue */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-600" /> Toplam Gelir
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(revenue_total).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(revenue_total).map(([cur, d]) => (
                  <div key={cur} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg" data-testid={`total-revenue-${cur}`}>
                    <span className="text-sm font-medium text-gray-600">{cur}</span>
                    <span className="text-2xl font-bold text-emerald-700">
                      {d.symbol}{d.amount.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm text-center py-6">Henüz gelir yok</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Exhibition Comparison Chart */}
      {exhibitionBarData.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" /> Fuar Bazlı Stand Dağılımı
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={Math.max(200, exhibitionBarData.length * 42)}>
              <BarChart
                data={exhibitionBarData}
                layout="vertical"
                margin={{ top: 0, right: 30, left: 10, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  width={100}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Satıldı" stackId="a" fill="#ef4444" />
                <Bar dataKey="Rezerve" stackId="a" fill="#f59e0b" />
                <Bar dataKey="Müsait" stackId="a" fill="#22c55e" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
            {/* Clickable exhibition links */}
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
              {exhibition_comparison.map(ex => (
                <button
                  key={ex.id}
                  onClick={() => navigate(`/organizer/report/${ex.id}`)}
                  className="text-xs text-teal-600 hover:text-teal-800 hover:underline flex items-center gap-1"
                >
                  {ex.name} <ArrowRight className="w-3 h-3" />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly Sales Chart + Salespeople */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Monthly Sales Area Chart */}
        {monthlyChartData.length > 0 ? (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="w-5 h-5 text-indigo-600" /> Aylık Satış Trendi
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={monthlyChartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#14b8a6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="Satış"
                    stroke="#14b8a6"
                    strokeWidth={2}
                    fill="url(#salesGrad)"
                    dot={{ fill: '#14b8a6', r: 3 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="w-5 h-5 text-indigo-600" /> Aylık Satış Trendi
              </CardTitle>
            </CardHeader>
            <CardContent className="flex items-center justify-center h-32">
              <p className="text-gray-400 text-sm">Henüz satış verisi yok</p>
            </CardContent>
          </Card>
        )}

        {/* Salespeople Bar Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="w-5 h-5 text-emerald-500" /> Satışçı Performansı
            </CardTitle>
          </CardHeader>
          <CardContent>
            {salesBarData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={salesBarData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="Satış" fill="#10b981" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Rezerve" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-400 text-sm text-center py-8">Henüz satış verisi yok</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Customers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-500" /> En Çok Ciro Yapan Firmalar
            </CardTitle>
          </CardHeader>
          <CardContent>
            {top_customers_revenue.length > 0 ? (
              <div className="space-y-2">
                {top_customers_revenue.slice(0, 5).map((c, i) => (
                  <div key={c.company_id} className="flex items-center justify-between py-1.5 border-b last:border-0">
                    <div className="flex items-center gap-2">
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white
                        ${i === 0 ? 'bg-amber-500' : i === 1 ? 'bg-gray-400' : i === 2 ? 'bg-amber-700' : 'bg-gray-300'}`}>{i + 1}</span>
                      <span className="text-sm font-medium truncate max-w-[140px]">{c.name}</span>
                    </div>
                    <div className="text-right">
                      {Object.entries(c.revenue || {}).map(([cur, amt]) => (
                        <div key={cur} className="text-xs font-semibold">{CURRENCY_SYMBOLS[cur] || cur}{amt.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}</div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-gray-400 text-sm text-center py-4">Veri yok</p>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-500" /> En Çok Fuara Katılan
            </CardTitle>
          </CardHeader>
          <CardContent>
            {top_customers_exhibitions.length > 0 ? (
              <div className="space-y-2">
                {top_customers_exhibitions.slice(0, 5).map((c, i) => (
                  <div key={c.company_id} className="flex items-center justify-between py-1.5 border-b last:border-0">
                    <div className="flex items-center gap-2">
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white
                        ${i === 0 ? 'bg-blue-500' : 'bg-gray-300'}`}>{i + 1}</span>
                      <span className="text-sm font-medium truncate max-w-[140px]">{c.name}</span>
                    </div>
                    <span className="text-sm font-bold">{c.exhibitions_count} fuar</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-gray-400 text-sm text-center py-4">Veri yok</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
